import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled, { useTheme } from "styled-components";
import Text from "common/Typography/Text";
import useMeasure from "utils/hooks/useMeasure";
import useFitText from "common/Text/useFitText.ts";
import { getTextWidth } from "common/Text/utils.ts";
import { FontWeightValue } from "@shared/common/Themes/typography.ts";

const DEFAULT_BASE_FONT_SIZE = 14;
const DEFAULT_MIN_FONT_SIZE = 10;
const DEFAULT_FONT_WEIGHT = 400;

interface AutoSizeTextProps {
  baseFontSizePx?: number;
  minFontSizePx?: number;
  fontWeight?: FontWeightValue;
  inheritFont?: boolean;
  style?: CSSProperties;
  className?: string;
  title?: string;
  children: ReactNode;
}

type MeasuredTextProps = Omit<AutoSizeTextProps, "inheritFont" | "children"> & {
  children: string;
};

const AutoSizeText = ({
  inheritFont,
  children,
  ...props
}: AutoSizeTextProps) => {
  if (!inheritFont && typeof children === "string")
    return <MeasuredText {...props}>{children}</MeasuredText>;
  const style = inheritFont
    ? props.style
    : { ...getOwnFont(props), ...props.style };
  return (
    <FitText {...props} style={style}>
      {children}
    </FitText>
  );
};

export default AutoSizeText;

const getOwnFont = ({
  baseFontSizePx = DEFAULT_BASE_FONT_SIZE,
  fontWeight = DEFAULT_FONT_WEIGHT,
}: Omit<AutoSizeTextProps, "children">): CSSProperties => ({
  fontSize: baseFontSizePx,
  fontWeight,
});

const getPixelSize = (size: CSSProperties["fontSize"]) => {
  if (typeof size === "number") return size;
  return typeof size === "string" && size.endsWith("px")
    ? parseFloat(size)
    : undefined;
};

const FitText = ({
  minFontSizePx = DEFAULT_MIN_FONT_SIZE,
  style,
  className,
  title,
  children,
}: Omit<AutoSizeTextProps, "inheritFont">) => {
  const { containerRef, contentRef } = useFitText<
    HTMLDivElement,
    HTMLDivElement
  >(minFontSizePx, `${style?.fontSize} ${style?.fontWeight}`);
  return (
    <FitContainer ref={containerRef} className={className}>
      <FitContent ref={contentRef} style={style} title={title}>
        {children}
      </FitContent>
    </FitContainer>
  );
};

const MeasuredText = (props: MeasuredTextProps) => {
  const { measureRef, measureStyle, textStyle, fontWeight } =
    useMeasuredText(props);
  return (
    <div ref={measureRef} className={props.className} style={measureStyle}>
      <Text
        $weight={fontWeight}
        $ellipsis={true}
        style={textStyle}
        title={props.title}
      >
        {props.children}
      </Text>
    </div>
  );
};

const useMeasuredText = ({
  baseFontSizePx = DEFAULT_BASE_FONT_SIZE,
  minFontSizePx: minSize = DEFAULT_MIN_FONT_SIZE,
  fontWeight = DEFAULT_FONT_WEIGHT,
  style,
  children,
}: MeasuredTextProps) => {
  const [measureRef, { width: maxWidth }] = useMeasure<HTMLDivElement>();
  const baseSize = getPixelSize(style?.fontSize) ?? baseFontSizePx;
  const measure = useTextMeasure(children, fontWeight);
  const fontSize = useFittingFontSize(measure, maxWidth, baseSize, minSize);
  const measureStyle = useMeasureStyle(style?.maxWidth);
  const textStyle = { ...style, fontSize, maxWidth };
  return { measureRef, measureStyle, textStyle, fontWeight };
};

const useMeasureStyle = (maxWidth: CSSProperties["maxWidth"]) =>
  useMemo(() => ({ width: "100%", maxWidth }), [maxWidth]);

const useTextMeasure = (text: string, fontWeight: FontWeightValue) => {
  const fontFamily = useTheme().typography.fontFamily;
  return useCallback(
    (size: number) => getTextWidth(text, size, fontWeight, fontFamily),
    [text, fontWeight, fontFamily]
  );
};

const useFittingFontSize = (
  measure: (size: number) => number,
  maxWidth: number,
  baseSize: number,
  minSize: number
) => {
  const fontsVersion = useFontsVersion();
  const [fontSize, setFontSize] = useState(baseSize);
  useEffect(() => {
    if (maxWidth > 0)
      setFontSize(getFittingFontSize(maxWidth, baseSize, minSize, measure));
  }, [measure, maxWidth, baseSize, minSize, fontsVersion]);
  return fontSize;
};

const getFittingFontSize = (
  maxWidth: number,
  baseSize: number,
  minSize: number,
  measure: (size: number) => number
) => {
  let size = baseSize;
  while (size > minSize && measure(size) > maxWidth) size -= 1;
  return size;
};

const useFontsVersion = () => {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((current) => current + 1);
    document.fonts?.addEventListener("loadingdone", bump);
    return () => document.fonts?.removeEventListener("loadingdone", bump);
  }, []);
  return version;
};

const FitContainer = styled.div`
  display: block;
  min-width: 0;
  max-width: 100%;
`;
const FitContent = styled.div`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
